import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Search, Plus, Filter } from 'lucide-react'

import { api, ApiError } from '@/lib/api/client'
import type { ProductArticle, ProductTypeUsage, DiscountRule } from '@/types/api'
import { DiscountRuleEditorModal, NewProductTypeModal } from './BillingModals'

// Fallback options shown when no product types are registered yet (fresh
// install). Kept narrow — finance can add the real list via the Product
// Types tab. These match the WI-12031 design seeds.
const FALLBACK_PRODUCT_TYPES = ['SYNC', 'REPORT'] as const

type Tab = 'articles' | 'rules' | 'types'

const TABS: { key: Tab; label: string }[] = [
  { key: 'articles', label: 'Article classification' },
  { key: 'rules',    label: 'Discount rules'         },
  { key: 'types',    label: 'Product types'          },
]

export default function SubscriptionSettingsPage() {
  const [tab, setTab] = useState<Tab>('articles')

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="page-eyebrow">Settings · Billing</div>
          <h1 className="page-title">Articles & rules</h1>
          <div className="page-subtitle">
            Classify Fortnox articles as product articles, manage discount rules, and curate the
            list of product types used across subscriptions.
          </div>
        </div>
      </div>

      <div className="tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'articles' && <ArticlesTab />}
      {tab === 'rules'    && <RulesTab />}
      {tab === 'types'    && <ProductTypesTab />}
    </div>
  )
}


// ── Articles tab ─────────────────────────────────────────────────────────────


function ArticlesTab() {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [productFilter, setProductFilter] = useState<'any' | 'true' | 'false'>('any')

  const { data: articles = [], isLoading } = useQuery<ProductArticle[]>({
    queryKey: ['articles', productFilter],
    queryFn:  () => api.get(`/articles${productFilter === 'any' ? '' : `?isProduct=${productFilter}`}`),
  })

  // Source the dropdown from the V065 managed list rather than deriving from
  // articles — fresh installs have no classified articles yet, so the old
  // derivation would only show the hardcoded fallback list.
  const { data: managedProductTypes = [] } = useQuery<ProductTypeUsage[]>({
    queryKey: ['product-types'],
    queryFn:  () => api.get('/product-types'),
    staleTime: 60_000,
  })

  const filtered = useMemo(() => {
    if (!search) return articles
    const q = search.toLowerCase()
    return articles.filter(a =>
      a.articleNumber.toLowerCase().includes(q) ||
      a.name.toLowerCase().includes(q),
    )
  }, [articles, search])

  // Merge managed list + any product_type values already on articles (legacy
  // free-text rows from before V065's FK) + the SYNC/REPORT fallbacks so a
  // brand-new install isn't a blank dropdown.
  const productTypes = useMemo(() => {
    const set = new Set<string>()
    for (const pt of managedProductTypes) set.add(pt.code)
    for (const a of articles) if (a.productType) set.add(a.productType)
    for (const fb of FALLBACK_PRODUCT_TYPES)    set.add(fb)
    return Array.from(set).sort()
  }, [articles, managedProductTypes])

  const mutation = useMutation({
    mutationFn: async (body: { id: number; isProductArticle: boolean; productType: string | null }) =>
      api.patch(`/articles/${body.id}/classification`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['articles'] }),
    onError:   (e) => toast.error(e instanceof ApiError ? e.message : 'Could not update article'),
  })

  function setIsProduct(a: ProductArticle, next: boolean) {
    if (next && !a.productType) {
      // Catch-22 guard: the backend requires a product_type, so direct the
      // user to the dropdown (which is now always enabled, see below).
      toast.error('Pick a product type from the dropdown first.')
      return
    }
    mutation.mutate({ id: a.id, isProductArticle: next, productType: next ? a.productType : null })
  }

  function setProductType(a: ProductArticle, type: string) {
    // Picking a type implicitly marks the article as a product — the user
    // doesn't need a separate checkbox click. The checkbox follows via the
    // refetch triggered by onSuccess.
    mutation.mutate({ id: a.id, isProductArticle: true, productType: type })
  }

  return (
    <>
      {/* Toolbar */}
      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', minWidth: 260 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ns-text-muted)' }} />
          <input
            className="input"
            placeholder="Search by article number or name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ paddingLeft: 30 }}
          />
        </div>
        <button
          className={`btn ${productFilter !== 'any' ? 'btn-brand' : ''}`}
          onClick={() => setProductFilter(productFilter === 'any' ? 'true' : productFilter === 'true' ? 'false' : 'any')}
        >
          <Filter size={13} /> Is product · {productFilter === 'any' ? 'Any' : productFilter === 'true' ? 'Yes' : 'No'}
        </button>
      </div>

      {/* Table */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Article #</th>
              <th>Name</th>
              <th style={{ textAlign: 'right' }}>Sales price</th>
              <th>Status</th>
              <th style={{ textAlign: 'center' }}>Is product</th>
              <th>Product type</th>
              <th style={{ textAlign: 'right' }}>Used by</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center' }} className="muted">Loading…</td></tr>}
            {!isLoading && filtered.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 36, textAlign: 'center' }} className="muted">
                No articles match the current filter.
              </td></tr>
            )}
            {filtered.map(a => (
              <tr key={a.id}>
                <td className="mono" style={{ fontSize: 12 }}>{a.articleNumber}</td>
                <td>{a.name}</td>
                <td className="tabular" style={{ textAlign: 'right' }}>
                  {a.salesPrice != null ? new Intl.NumberFormat('sv-SE').format(a.salesPrice) : '—'}
                </td>
                <td>
                  {a.isActiveInFortnox ? (
                    <span className="badge badge-ok"><span className="dot dot-ok" /> Active</span>
                  ) : (
                    <span className="badge"><span className="dot dot-muted" /> Inactive</span>
                  )}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={a.isProductArticle}
                    onChange={e => setIsProduct(a, e.target.checked)}
                  />
                </td>
                <td>
                  {/* Always enabled — picking a type implicitly marks the
                      article as a product, which sidesteps the original
                      catch-22 (couldn't pick a type because the dropdown
                      was disabled, couldn't enable the dropdown because no
                      type was set). */}
                  <select
                    className="input"
                    value={a.productType ?? ''}
                    onChange={e => e.target.value && setProductType(a, e.target.value)}
                    style={{ minWidth: 140 }}
                  >
                    <option value="">— pick —</option>
                    {productTypes.map(pt => <option key={pt} value={pt}>{pt}</option>)}
                  </select>
                </td>
                <td className="tabular" style={{ textAlign: 'right', fontSize: 12 }}>
                  {a.activeLineCount > 0 ? `${a.activeLineCount} subs` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="card" style={{ padding: 14, marginTop: 14, background: 'var(--ns-surface-2)' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>How classification works</div>
        <div className="muted" style={{ fontSize: 12.5 }}>
          Marking an article as <strong>product</strong> makes it available when adding a subscription line. The classification
          is set in Forge and preserved across Fortnox re-syncs — it never gets overwritten by the next sync.
          Articles that are no longer <strong>active in Fortnox</strong> can't be picked for new lines but continue to bill existing ones.
        </div>
      </div>
    </>
  )
}


// ── Discount rules tab ───────────────────────────────────────────────────────


function RulesTab() {
  const { data: rules = [], isLoading } = useQuery<DiscountRule[]>({
    queryKey: ['discount-rules'],
    queryFn:  () => api.get('/discount-rules'),
  })
  // null  → no modal open
  // ''    → "new rule" mode
  // rule  → editing an existing rule
  const [editing, setEditing] = useState<DiscountRule | null | ''>(null)

  return (
    <>
      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
        <div className="muted" style={{ fontSize: 12.5, alignSelf: 'center' }}>
          {rules.length} rule{rules.length === 1 ? '' : 's'} configured
        </div>
        <button className="btn btn-brand" onClick={() => setEditing('')}>
          <Plus size={13} /> New rule
        </button>
      </div>

      {isLoading && <div className="card muted" style={{ padding: 24, textAlign: 'center' }}>Loading…</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 12 }}>
        {rules.map(r => <RuleCard key={r.id} rule={r} onEdit={() => setEditing(r)} />)}
      </div>

      {editing !== null && (
        <DiscountRuleEditorModal
          rule={editing === '' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}

      <div className="card" style={{ padding: 14, marginTop: 14, background: 'var(--ns-surface-2)' }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>Stacking order</div>
        <div className="muted" style={{ fontSize: 12.5 }}>
          Discounts apply in a fixed order at invoice time: <strong>line-level</strong> first, then
          <strong> subscription-level percentage / fixed amount</strong>, finally
          <strong> volume-tier</strong> on the post-discount sum. This protects earned volume benefits
          from being eroded by special line-level carve-outs.
        </div>
      </div>
    </>
  )
}


function RuleCard({ rule, onEdit }: { rule: DiscountRule; onEdit: () => void }) {
  let summary = '—'
  if (rule.ruleType === 'PERCENTAGE' && rule.flatPercent != null) summary = `${rule.flatPercent}% off`
  else if (rule.ruleType === 'FIXED_AMOUNT' && rule.flatAmount != null)
    summary = `${new Intl.NumberFormat('sv-SE').format(rule.flatAmount)} ${rule.flatAmountCurrency} off`
  else if (rule.ruleType === 'VOLUME_TIER' && rule.tierBands.length > 0)
    summary = rule.tierBands.map(b => {
      const upper = b.toCountInclusive ?? '∞'
      return `${b.discountPercent}% (${b.fromCount}–${upper})`
    }).join(' · ')

  const typeLabel = rule.ruleType === 'VOLUME_TIER' ? 'Volume tier'
    : rule.ruleType === 'PERCENTAGE' ? 'Flat percent' : 'Fixed amount'

  return (
    <div className="card" style={{ padding: 16 }}>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{rule.name}</div>
          <div className="muted" style={{ fontSize: 11.5, marginTop: 2 }}>{typeLabel}</div>
        </div>
        <span className="badge badge-info" style={{ fontSize: 11 }}>
          In use × {rule.usedByCount}
        </span>
      </div>
      {rule.description && (
        <div className="muted" style={{ fontSize: 12.5, marginTop: 10 }}>{rule.description}</div>
      )}
      <div className="mono" style={{ fontSize: 11.5, marginTop: 10, background: 'var(--ns-surface-2)', padding: '8px 10px', borderRadius: 6 }}>
        {summary}
      </div>
      {rule.ruleType === 'VOLUME_TIER' && rule.tierBands.length > 0 && (
        <TierBar bands={rule.tierBands} />
      )}
      <div className="row" style={{ gap: 6, marginTop: 12 }}>
        <button className="btn btn-sm" onClick={onEdit}>Edit</button>
        {rule.archivedAt && <span className="muted" style={{ fontSize: 11, marginLeft: 'auto' }}>Archived</span>}
      </div>
    </div>
  )
}


function TierBar({ bands }: { bands: DiscountRule['tierBands'] }) {
  // Each band gets a flex weight roughly proportional to its size; "and above" gets 1.5x to suggest open-ended.
  const sized = bands.map(b => {
    const upper = b.toCountInclusive ?? b.fromCount + 15
    return { ...b, weight: Math.max(1, upper - b.fromCount + 1) }
  })
  const total = sized.reduce((s, b) => s + b.weight, 0)

  return (
    <div style={{ display: 'flex', height: 18, marginTop: 10, borderRadius: 6, overflow: 'hidden', gap: 2 }}>
      {sized.map((b, i) => {
        const color = b.discountPercent === 0
          ? 'var(--ns-text-subtle)'
          : b.discountPercent < 15
            ? 'var(--ns-brand-strong)'
            : 'oklch(0.62 0.13 50)'
        return (
          <div key={i} title={`${b.fromCount}–${b.toCountInclusive ?? '∞'} → ${b.discountPercent}%`}
            style={{
              flex: b.weight / total, background: color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, fontWeight: 700, color: 'white',
            }}>
            {b.discountPercent}%
          </div>
        )
      })}
    </div>
  )
}


// ── Product types tab ────────────────────────────────────────────────────────


function ProductTypesTab() {
  const { data: types = [], isLoading } = useQuery<ProductTypeUsage[]>({
    queryKey: ['product-types'],
    queryFn:  () => api.get('/product-types'),
  })
  const [showNew, setShowNew] = useState(false)

  return (
    <>
      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="muted" style={{ fontSize: 12.5 }}>
          {types.length} type{types.length === 1 ? '' : 's'} in the managed list
        </div>
        <button className="btn btn-brand" onClick={() => setShowNew(true)}>
          <Plus size={13} /> New product type
        </button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Display name</th>
              <th style={{ textAlign: 'right' }}>Articles</th>
              <th style={{ textAlign: 'right' }}>Active lines</th>
              <th>Default revenue category</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} style={{ padding: 24, textAlign: 'center' }} className="muted">Loading…</td></tr>}
            {!isLoading && types.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 36, textAlign: 'center' }} className="muted">
                No product types yet. Click <strong>New product type</strong> to add the first one.
              </td></tr>
            )}
            {types.map(t => (
              <tr key={t.code}>
                <td>
                  <span style={{
                    display: 'inline-flex', padding: '2px 8px', borderRadius: 999,
                    fontSize: 11, fontWeight: 700, letterSpacing: '0.04em',
                    background: 'color-mix(in oklch, var(--ns-brand-strong) 14%, transparent)',
                    color: 'var(--ns-brand-strong)',
                  }}>{t.code}</span>
                </td>
                <td>
                  <div>{t.displayName}</div>
                  {t.description && (
                    <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{t.description}</div>
                  )}
                </td>
                <td className="tabular" style={{ textAlign: 'right' }}>{t.articleCount}</td>
                <td className="tabular" style={{ textAlign: 'right' }}>{t.activeLineCount}</td>
                <td><span className="badge">PRODUCT</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card" style={{ padding: 14, marginTop: 14, background: 'var(--ns-surface-2)' }}>
        <div className="muted" style={{ fontSize: 12.5 }}>
          Product types are a managed list — once you add one here it appears in the Articles tab dropdown
          so finance can classify articles under it. Every product type resolves to the <code className="mono">PRODUCT</code>
          revenue category via the WI-11973 matrix at invoice time.
        </div>
      </div>

      {showNew && <NewProductTypeModal onClose={() => setShowNew(false)} />}
    </>
  )
}
